import React, {useState} from 'react';

import {useNavigate} from 'react-router';

import {useAction, useActions, useSelectorWithRefresh} from '@/store/kysely_module';
import {useModule} from '@/hooks/useModule';
import {DBObjects} from '@/store/store_types';

import {OfflineBetaMessage} from '../components/offline_beta_message';
import {useModals} from '../components/modals/modals_provider';
import {StripedTable} from '../components/striped_table';
import {RecentActivity} from '../components/recent_activity';
import {WorkspaceType} from '@/store/kysely_types';
import {Accordion, Button, Input, Radio, RadioGroup, Container, Card, Title, Group, Stack, Table, ActionIcon, Grid} from '@mantine/core';
import {useInitalFocus} from '@/hooks/useInitalFocus';
import {IconUser, IconBuilding, IconPlus} from '@tabler/icons-react';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';
import {useIsMobile} from '@/utils/user_agent';
import {DashboardCard} from '@/components/new_dashboard/components/DashboardCard';
import {DashboardLayout} from '@/components/new_dashboard/components/DashboardLayout';
import {Selectors} from '@/store/selectors/selectors';

export const WorkspaceSelectionPage = () => {
    const [workspaces, refreshWorkspaces] = useSelectorWithRefresh('getAllWorkspacesWithStats', {}, [], {workspace_id: null});
    const navigate = useNavigate();

    const workspaceModule = useModule('Workspaces');

    const modals = useModals();

    const actions = {
        createWorkspace: useAction('createWorkspace'),
    };

    const navigateToWorkspace = (workspaceId: string) => {
        workspaceModule.setCurrentWorkspaceId(workspaceId);
        navigateToPathForReason(navigate, 'navigate_to_projects_page_from_workspace_selection_page', `/workspaces/${workspaceId}/projects`);
    };

    const userModule = useModule('User');
    const isOfflineMode = userModule.isOfflineMode.useState();
    const isMobile = useIsMobile();

    const createNewWorkspace = async () => {
        const defaultHideLocalWorkspace = (globalThis as unknown as { HIDE_LOCAL_WORKSPACE_IF_NOT_OFFLINE_MODE?: boolean }).HIDE_LOCAL_WORKSPACE_IF_NOT_OFFLINE_MODE === true;
        const shouldShowLocalOption = !defaultHideLocalWorkspace || isOfflineMode;
        const shouldShowRemoteOptions = !isOfflineMode;

        const workspaceTypes: WorkspaceType[] = [];
        if (shouldShowRemoteOptions) {
            workspaceTypes.push('SHARED', 'PERSONAL');
        }

        if (shouldShowLocalOption) {
            workspaceTypes.push('LOCAL');
        }

        modals.openModal('CustomModal', {
            title: 'New workspace',
            form: ({close}) => {
                return (
                    <>
                        <CreateNewWorkspaceForm
                            onSubmit={async (name, type) => {
                                const workspace = await actions.createWorkspace({name, type}, {workspace_id: null});

                                const knownWorkspaces = workspaceModule.knownWorkspacesState.getState();
                                workspaceModule.setKnownWorkspaces([
                                    ...knownWorkspaces,
                                    {workspace, type: type === 'LOCAL' ? ('local' as const) : ('remote' as const)},
                                ]);

                                refreshWorkspaces();
                                navigateToWorkspace(workspace.id);
                            }}
                            workspaceTypes={workspaceTypes}
                            close={close}
                        />
                    </>
                )
            },
        });
    };

    return (
        <div style={{
            width: '100%',
            padding: isMobile ? '0.5rem' : '2rem',
            minHeight: '70vh'
        }}>
            <div style={{
                width: '100%',
                minWidth: isMobile ? 'auto' : '800px',
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                <DashboardCard
                    title="Select Workspace"
                    icon={<IconBuilding size={18} />}
                    headerActions={
                        <Button
                            size="xs"
                            leftSection={<IconPlus size={16} />}
                            onClick={createNewWorkspace}
                        >
                            New Workspace
                        </Button>
                    }
                >
                    <WorkspaceTable
                        workspaces={workspaces}
                        onClick={workspaceId => navigateToWorkspace(workspaceId)}
                    />
                </DashboardCard>

                <div style={{marginTop: '2rem'}}>
                    <OfflineBetaMessage />
                </div>
            </div>
        </div>
    )
};

type WorkspaceTableProps = {
    workspaces: Awaited<ReturnType<Selectors['getAllWorkspacesWithStats']>>;
    onClick: (workspaceId: string) => void;
}

const WorkspaceTable = (props: WorkspaceTableProps) => {
    const isMobile = useIsMobile();

    if (props.workspaces.length === 0) {
        return (
            <Stack align="center" py="xl">
                <Title order={5} c="dimmed">No workspaces yet</Title>
            </Stack>
        );
    }

    return (
        <Table striped highlightOnHover withRowBorders>
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th style={{width: isMobile ? '80px' : '120px', textAlign: 'right'}}>Members</Table.Th>
                    <Table.Th style={{width: isMobile ? '80px' : '120px', textAlign: 'right'}}>Projects</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {props.workspaces.map((workspace) => {
                    const isLocal = workspace.type === 'LOCAL';
                    const displayName = isLocal ? `${workspace.name} (Local)` : workspace.name;

                    return (
                        <Table.Tr
                            key={workspace.id}
                            style={{cursor: 'pointer'}}
                            onClick={() => props.onClick(workspace.id)}
                            className='workspace-table-row'
                        >
                            <Table.Td>
                                <Group gap="xs">
                                    <IconBuilding size={16} />
                                    <span style={{fontSize: isMobile ? '0.875rem' : '1rem'}}>
                                        {displayName}
                                    </span>
                                </Group>
                            </Table.Td>
                            <Table.Td style={{textAlign: 'right'}}>
                                <span style={{fontSize: isMobile ? '0.875rem' : '1rem'}}>
                                    {workspace.memberCount}
                                </span>
                            </Table.Td>
                            <Table.Td style={{textAlign: 'right'}}>
                                <span style={{fontSize: isMobile ? '0.875rem' : '1rem'}}>
                                    {workspace.projectCount}
                                </span>
                            </Table.Td>
                        </Table.Tr>
                    );
                })}
            </Table.Tbody>
        </Table>
    );
};

type CreateNewWorkspaceFormProps = {
    onSubmit: (name: string, type: WorkspaceType) => void;
    workspaceTypes: WorkspaceType[];
    close: () => void;
};

const CreateNewWorkspaceForm = (props: CreateNewWorkspaceFormProps) => {
    const [workspaceName, setWorkspaceName] = useState('');
    const [workspaceType, setWorkspaceType] = useState<WorkspaceType>(props.workspaceTypes[0] || 'SHARED');
    const inputRef = useInitalFocus();

    const handleSubmit = async () => {
        props.onSubmit(workspaceName, workspaceType);
        props.close();
    };

    return (
        <div>
            <div style={{margin: '10px'}}>
                <p>
                    Workspace Name
                </p>
                <Input
                    ref={inputRef}
                    onChange={e => setWorkspaceName(e.target.value)}
                    value={workspaceName}
                />
            </div>

            <div style={{margin: '10px'}}>
                <p>
                    Workspace Type
                </p>
                {/* mantine radios */}

                <RadioGroup
                    value={workspaceType}
                    variant="vertical"
                    onChange={e => setWorkspaceType(e as WorkspaceType)}
                // label="Select your favorite framework/library"
                // description="This is anonymous"
                >
                    {props.workspaceTypes.includes(WorkspaceType.SHARED) && <Radio value={WorkspaceType.SHARED} label='Shared' />}
                    {props.workspaceTypes.includes(WorkspaceType.PERSONAL) && <Radio value={WorkspaceType.PERSONAL} label='Personal' />}
                    {props.workspaceTypes.includes(WorkspaceType.LOCAL) && <Radio value={WorkspaceType.LOCAL} label='Local' />}
                </RadioGroup>
            </div>

            <Button
                disabled={!workspaceName}
                onClick={handleSubmit}
            >
                Submit
            </Button>
            <Button
                style={{marginLeft: '10px'}}
                onClick={props.close}
            >
                Cancel
            </Button>
        </div>
    );
};
